import { SEAT_ADDON_MAX, SEAT_ADDON_MIN, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Dialog, DialogContent, Icon, Progress, Skeleton, Slider, Tooltip, cn } from "@sagot/ui";
import { useState } from "react";
import { FormAlert } from "../auth/components/form-alert";
import { yearContext } from "../features/workspace/shared";
import type { OrderOut } from "../features/workspace/shared/api-types";
import { useErrorMessage } from "../lib/api-errors";
import { usePermissions } from "../session/permissions";
import { useWorkspaceContext } from "../session/workspace-context";
import type { SeatsInfo } from "./use-team";
import { useSeatPrice } from "./use-seat-price";
import { useAddSeats } from "./use-team";

interface AddSeatsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOrder: (order: OrderOut, quantity: number) => void;
}

/** `seats-modal`: extra seats slider, price per seat, "or upgrade" link → `POST /v1/billing/addons`. */
export function AddSeatsDialog({ open, onOpenChange, onOrder }: AddSeatsDialogProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const message = useErrorMessage();
  const add = useAddSeats();
  const price = useSeatPrice();
  const [quantity, setQuantity] = useState(SEAT_ADDON_MIN);
  const submit = () => {
    add.mutate(quantity, {
      onSuccess: (order) => {
        onOpenChange(false);
        onOrder(order, quantity);
      },
    });
  };
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) add.reset(); onOpenChange(next); }}>
      <DialogContent
        title={t("team.seats.addSeatsTitle")}
        closeLabel={t("common.close")}
        className="max-w-md"
        footer={
          <>
            <Button variant="secondary" onClick={() => { onOpenChange(false); }}>
              {t("team.cancel")}
            </Button>
            <Button loading={add.isPending} onClick={submit}>
              {t("team.seats.addSeats")}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {add.isError ? <FormAlert title={message(add.error)} /> : null}
          <Slider label={t("team.seats.extraSeats")} value={quantity} onValueChange={setQuantity} min={SEAT_ADDON_MIN} max={SEAT_ADDON_MAX} />
          <div className="rounded-md bg-surface-2 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-fg-muted">{t("team.seats.perSeat")}</span>
              <span className="font-semibold">{price ? t(price.per === "year" ? "team.seats.perYear" : "team.seats.perMonth", { price: price.format(price.unit) }) : "—"}</span>
            </div>
            <div className="mt-1 flex justify-between gap-3">
              <span className="text-fg-muted">{price?.per === "year" ? (price.proration ? t("team.seats.chargedNowDays", { days: price.proration.daysRemaining, total: price.proration.cycleDays }) : t("team.seats.chargedNow")) : t("team.seats.totalMonthly")}</span>
              <span className="font-bold tabular-nums">{price ? (price.per === "year" ? price.format(price.dueNow * quantity) : t("team.seats.perMonth", { price: price.format(price.unit * quantity) })) : "—"}</span>
            </div>
            <div className="mt-1 flex justify-between">
              <span className="text-fg-muted">{t("team.seats.orUpgrade")}</span>
              <AppLink href={consoleRoutes.billing(summary.slug)} className="font-semibold text-primary-ink hover:underline">
                {t("team.seats.seePlans")}
              </AppLink>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SeatsNote({ seats }: { seats: NonNullable<SeatsInfo["state"]> }) {
  const { t } = useTranslation();
  if (seats.available === null) return null;
  if (seats.pending > seats.available) return <>{t("team.seats.pendingNeedsSeat", { count: seats.pending - seats.available })}</>;
  if (seats.available === 0) return <>{t("team.seats.full")}</>;
  return <>{t("team.seats.available", { count: seats.available })}</>;
}

/** Seat usage (warning tone when full) + "Add seats <price>/seat/month" (billing.manage). */
export function SeatsCard({ info, onAddSeats }: { info: SeatsInfo; onAddSeats: () => void }) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const price = useSeatPrice();
  if (!info.state) {
    return (
      <section className="card card-body flex items-center gap-4" aria-hidden="true">
        <Skeleton className="size-10" />
        <Skeleton className="h-4 w-64" />
        <Skeleton className="h-2 flex-1" />
      </section>
    );
  }
  const seats = info.state;
  const plan = info.plan ? t(`console.plans.${info.plan}`, { defaultValue: info.plan }) : "";
  const title = seats.limit === null ? t("team.seats.titleUnlimited", { used: seats.used, plan }) : t("team.seats.title", { used: seats.used, limit: seats.limit, plan });
  const canAdd = permissions.can("billing.manage") && !permissions.readOnly;
  const button = (
    <Button variant="secondary" size="sm" disabled={!canAdd} onClick={onAddSeats}>
      <Icon name="plus" />
      {t("team.seats.addSeats")}
    </Button>
  );
  return (
    <section className="card card-body flex flex-col gap-4 md:flex-row md:items-center" aria-label={t("team.seats.meterLabel")} data-testid="seats-card">
      <div className="flex items-center gap-3">
        <span className={cn("inline-flex size-10 shrink-0 items-center justify-center rounded-lg", seats.full ? "bg-warning-soft text-warning" : "bg-primary-soft text-primary-ink")}>
          <Icon name={seats.full ? "alert-triangle" : "users"} className="size-5" />
        </span>
        <div>
          <div className="text-sm font-bold">{title}</div>
          <div className="text-xs text-fg-muted">
            <SeatsNote seats={seats} />
          </div>
        </div>
      </div>
      {seats.limit !== null ? (
        <div className="flex-1 md:px-6">
          <Progress value={Math.min(seats.used, seats.limit)} max={seats.limit} tone={seats.full ? "warning" : "primary"} label={t("team.seats.meterLabel")} valueText={title} />
        </div>
      ) : (
        <div className="flex-1" />
      )}
      {seats.limit !== null ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-fg-muted">
            {t("team.seats.addSeats")} {price ? <strong className="text-fg">{t("team.seats.pricePerSeat", { price: price.format(price.unit), ...yearContext(price.per) })}</strong> : null}
          </span>
          {canAdd ? button : <Tooltip content={t("team.seats.noBillingAccess")}>{<span className="inline-flex">{button}</span>}</Tooltip>}
        </div>
      ) : null}
    </section>
  );
}
